import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetcher, useLoaderData } from '@remix-run/react';

import type { ReviewSummariesResponse, ReviewSummary } from '~/services/api/products/getProductReviewSummaries.server';
import type { StaticPageLoaderData } from '~/services/static_page';

interface UseProductReviewSummariesArgs {
  productIds: string[];
  enabled?: boolean;
}

interface UseProductReviewSummariesReturn {
  summaries: Record<string, ReviewSummary | null>;
  isLoading: boolean;
  refetch: () => void;
}

const useProductReviewSummaries = ({
  productIds,
  enabled = true,
}: UseProductReviewSummariesArgs): UseProductReviewSummariesReturn => {
  const { productReviewSummaries: loaderSummaries, hasProductReviewSummariesDataFetched } =
    useLoaderData<StaticPageLoaderData>();
  const fetcher = useFetcher<ReviewSummariesResponse>();
  const [summaries, setSummaries] = useState<Record<string, ReviewSummary | null>>({});
  const prevProductIdsRef = useRef<string>('');
  const initializedFromLoaderRef = useRef(false);

  // Initialize from loader data if available
  useEffect(() => {
    if (hasProductReviewSummariesDataFetched && loaderSummaries && !initializedFromLoaderRef.current) {
      initializedFromLoaderRef.current = true;
      setSummaries((prev) => ({
        ...prev,
        ...loaderSummaries,
      }));
    }
  }, [hasProductReviewSummariesDataFetched, loaderSummaries]);

  const fetchSummaries = useCallback(() => {
    if (!enabled || !productIds.length) return;

    // Only fetch IDs that aren't already in summaries
    const missingIds = productIds.filter((id) => !(id in summaries));
    if (missingIds.length === 0) return;

    const searchParams = new URLSearchParams();
    for (const id of missingIds) {
      searchParams.append('product_ids[]', id);
    }

    fetcher.load(`/product_review_summaries?${searchParams.toString()}`);
  }, [enabled, productIds, summaries, fetcher]);

  // Fetch when product IDs change (only for missing IDs)
  useEffect(() => {
    const currentIds = [...productIds].sort().join(',');
    if (currentIds !== prevProductIdsRef.current && enabled && productIds.length > 0) {
      prevProductIdsRef.current = currentIds;
      // Only fetch if loader hasn't provided data or there are missing IDs
      const missingIds = productIds.filter((id) => !(id in summaries));
      if (missingIds.length > 0) {
        fetchSummaries();
      }
    }
  }, [productIds, enabled, summaries, fetchSummaries]);

  // Update state when fetcher data arrives
  useEffect(() => {
    if (fetcher.data?.summaries) {
      setSummaries((prev) => ({
        ...prev,
        ...fetcher.data?.summaries,
      }));
    }
  }, [fetcher.data]);

  return {
    summaries,
    isLoading: fetcher.state !== 'idle',
    refetch: fetchSummaries,
  };
};

export default useProductReviewSummaries;
